import {
  Briefcase,
  Building2,
  IndianRupee,
  MapPin,
  Save,
  Send,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Domain, Job, JobStatusEnum } from "../../swagger";
import { adminJobApi, domainApi } from "../../utils/configuration";

export default function ViewJobComponent() {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [isLoadingJob, setIsLoadingJob] = useState(false);
  const [jobStatus, setJobStatus] = useState<JobStatusEnum | null>(null);

  const { id } = useParams();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<Job>();

  // Watch form values for preview and dependent selects
  const watchedValues = watch();
  const selectedDomainId = watch("domainId");
  const selectedCategoryId = watch("categoryId");

  // Get selected domain and category for dependent dropdowns
  const selectedDomain = domains.find((d) => d.id === selectedDomainId);
  const selectedCategory = selectedDomain?.Categories?.find(
    (c) => c.id === selectedCategoryId
  );

  // Check if job is in draft status for publish button
  const canPublish = jobStatus === JobStatusEnum.Draft;

  const isLoadingInitialData = useRef(false);

  // API call to fetch domains
  const fetchDomains = useCallback(async () => {
    try {
      const fetchedDomains = await domainApi.getAllDomains();
      setDomains(fetchedDomains);
    } catch (error) {
      console.error("Error fetching domains:", error);
      await Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to load domains. Please refresh the page.",
        confirmButtonColor: "#4f46e5",
      });
    }
  }, []);

  // Fill form with job data
  const fillFormWithJobData = useCallback((job: Job) => {
    setJobStatus(job.status || null);
    Object.keys(job).forEach((key) => {
      const value = job[key as keyof Job];
      if (value !== undefined && value !== null) {
        setValue(key as keyof Job, value);
      }
    });

    setTimeout(() => {
      isLoadingInitialData.current = false;
    }, 100);
  }, [setValue, isLoadingInitialData]);

  // API call to fetch job by ID
  const fetchJobById = useCallback(async (jobId: string) => {
    try {
      setIsLoadingJob(true);
      isLoadingInitialData.current = true;
      const response = await adminJobApi.adminJobById({
        id: Number(jobId),
      });
      return response;
    } catch (error) {
      console.error("Error fetching job:", error);
      await Swal.fire({
        icon: "error",
        title: "Job Not Found!",
        text: "The job you are looking for does not exist or has been removed.",
        confirmButtonColor: "#4f46e5",
      });
      navigate("/jobs"); // Redirect to jobs list
      throw error;
    } finally {
      setIsLoadingJob(false);
    }
  }, [navigate, isLoadingInitialData]);

  // API call to update job post
  const updateJobPost = async (data: Job) => {
    try {
      const response = await adminJobApi.adminJobUpdate({
        id: Number(id),
        job: { ...data, status: jobStatus! },
      });

      return { success: true, data: response };
    } catch (error) {
      console.error("Error updating job post:", error);
      throw error;
    }
  };

  // API call to publish job
  const publishJob = async () => {
    try {
      const response = await adminJobApi.adminJobPublish({
        id: Number(id),
      });

      return { success: true, data: response };
    } catch (error) {
      console.error("Error publishing job:", error);
      throw error;
    }
  };
  useEffect(() => {
    fetchDomains();
    if (id) {
      fetchJobById(id)
        .then((job) => {
          if (job) {
            fillFormWithJobData(job);
          }
        })
        .catch(() => {});
    }
  }, [id, fetchDomains, fetchJobById, fillFormWithJobData]);

  // Reset dependent fields when parent changes
  useEffect(() => {
    if (!isLoadingInitialData.current && selectedDomainId) {
      setValue("categoryId", undefined);
      setValue("subCategoryId", undefined);
    }
  }, [selectedDomainId, setValue]);

  useEffect(() => {
    if (!isLoadingInitialData.current && selectedCategory) {
      setValue("subCategoryId", undefined);
    }
  }, [selectedCategoryId, selectedCategory, setValue]);

  const handleSelectChange = (field: keyof Job, value: string) => {
    const numberFields: (keyof Job)[] = [
      "domainId",
      "categoryId",
      "subCategoryId",
    ];
    setValue(field, numberFields.includes(field) ? Number(value) : value);
  };

  const onSubmit = async (data: Job) => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Update Job Post?",
      text: "Are you sure you want to update this job post?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Update!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      // Show loading
      Swal.fire({
        title: "Updating...",
        text: "Please wait while we update your job post.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await updateJobPost(data);

      // Success message
      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Job post updated successfully!",
        confirmButtonColor: "#4f46e5",
      });

      // Reload the component to get fresh data
      window.location.reload();
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to update job post. Please try again.",
        confirmButtonColor: "#4f46e5",
      });
    }
  };

  const handlePublish = async () => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Publish Job Post?",
      text: "Once published, this job will be visible to all job seekers. Make sure to save all your changes before publishing. Are you sure?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Publish!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      // Show loading
      Swal.fire({
        title: "Publishing...",
        text: "Please wait while we publish your job post.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await publishJob();

      // Success message
      await Swal.fire({
        icon: "success",
        title: "Published!",
        text: "Job post published successfully! It is now visible to job seekers.",
        confirmButtonColor: "#4f46e5",
      });

      // Update local state and reload
      setJobStatus(JobStatusEnum.Published);
      window.location.reload();
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to publish job post. Please try again.",
        confirmButtonColor: "#4f46e5",
      });
    }
  };

  const handleCancel = async () => {
    const result = await Swal.fire({
      title: "Discard Changes?",
      text: "Are you sure you want to discard all changes? This will reload the original job data.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Discard",
      cancelButtonText: "Keep Editing",
    });

    if (result.isConfirmed) {
      // Reload the component to restore original data
      window.location.reload();
    }
  };

  // Show loading screen while fetching job data
  if (isLoadingJob) {
    return (
      <div className="lg:pl-64 pt-20 w-full mx-auto flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading job details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:pl-64 pt-20 w-full mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Job Post</h1>
          <p className="text-gray-600 mt-1">Update your job posting</p>
          {jobStatus && (
            <div className="mt-2">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  jobStatus === JobStatusEnum.Published
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                Status:{" "}
                {jobStatus === JobStatusEnum.Published ? "Published" : "Draft"}
              </span>
            </div>
          )}
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            disabled={isSubmitting}
          >
            <Save className="h-4 w-4 mr-2" />
            {isSubmitting ? "Updating..." : "Update Job"}
          </Button>
          {canPublish && (
            <Button
              onClick={handlePublish}
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={isSubmitting}
            >
              <Send className="h-4 w-4 mr-2" />
              Publish Job
            </Button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Briefcase className="h-5 w-5 mr-2 text-indigo-600" />
                  Job Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    {...register("title", {
                      required: "Job title is required",
                    })}
                    placeholder="e.g. Senior Full Stack Developer"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.title.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="jobType">Job Type *</Label>
                    <Select
                      value={watchedValues.type}
                      onValueChange={(value) =>
                        handleSelectChange("type", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select job type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FULLTIME">Full-time</SelectItem>
                        <SelectItem value="PARTIME">Part-time</SelectItem>
                        <SelectItem value="CONTRACT">Contract</SelectItem>
                        <SelectItem value="FREELANCE">Freelance</SelectItem>
                        <SelectItem value="INTERNSHIP">Internship</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.type && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.type.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="experienceLevel">Experience Level *</Label>
                    <Select
                      value={watchedValues.experienceLevel}
                      onValueChange={(value) =>
                        handleSelectChange("experienceLevel", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select experience level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ENTRY">
                          Entry Level (0-2 years)
                        </SelectItem>
                        <SelectItem value="MID">
                          Mid Level (2-5 years)
                        </SelectItem>
                        <SelectItem value="SENIOR">
                          Senior Level (5-8 years)
                        </SelectItem>
                        <SelectItem value="LEAD">
                          Lead Level (8+ years)
                        </SelectItem>
                        <SelectItem value="EXECUTIVE">
                          Executive Level
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.experienceLevel && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.experienceLevel.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="location">Location *</Label>
                    <Input
                      id="location"
                      {...register("location", {
                        required: "Location is required",
                      })}
                      placeholder="e.g. New York, NY or Remote"
                    />
                    {errors.location && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.location.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label>Salary Range *</Label>
                    <div className="flex items-center space-x-2">
                      <Select value={"INR"} disabled>
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">$</SelectItem>
                          <SelectItem value="EUR">€</SelectItem>
                          <SelectItem value="GBP">£</SelectItem>
                          <SelectItem value="INR">₹</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        placeholder="Min"
                        {...register("salaryStart", {
                          required: "Min. salary is required",
                        })}
                        className="flex-1"
                      />
                      <span className="text-gray-500">to</span>
                      <Input
                        type="number"
                        placeholder="Max"
                        {...register("salaryEnd", {
                          required: "Max. salary is required",
                        })}
                        className="flex-1"
                      />
                    </div>
                    {(errors.salaryStart || errors.salaryEnd) && (
                      <p className="text-red-500 text-sm mt-1">
                        Both salary range values are required
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Category Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Job Category</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="domain">Domain *</Label>
                    <Select
                      value={watchedValues.domainId?.toString() ?? ""}
                      onValueChange={(value) =>
                        handleSelectChange("domainId", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select domain" />
                      </SelectTrigger>
                      <SelectContent>
                        {domains.map((domain) => (
                          <SelectItem
                            key={domain.id}
                            value={domain.id?.toString() ?? ""}
                          >
                            {domain.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.domainId && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.domainId.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select
                      value={watchedValues.categoryId?.toString()}
                      onValueChange={(value) =>
                        handleSelectChange("categoryId", value)
                      }
                      disabled={!watchedValues.domainId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedDomain?.Categories?.map((category) => (
                          <SelectItem
                            key={category.id}
                            value={category.id?.toString() ?? ""}
                          >
                            {category.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.categoryId && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.categoryId.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="subcategory">Subcategory</Label>
                    <Select
                      value={watchedValues.subCategoryId?.toString() ?? ""}
                      onValueChange={(value) =>
                        handleSelectChange("subCategoryId", value)
                      }
                      disabled={!watchedValues.categoryId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select subcategory" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedCategory?.SubCategories?.map((subcategory) => (
                          <SelectItem
                            key={subcategory.id}
                            value={subcategory.id?.toString() ?? ""}
                          >
                            {subcategory.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Job Description */}
            <Card>
              <CardHeader>
                <CardTitle>Job Description</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="jobDescription">Job Description *</Label>
                  <Textarea
                    id="jobDescription"
                    {...register("description", {
                      required: "Job description is required",
                    })}
                    placeholder="Describe the role, responsibilities, and what the candidate will be doing..."
                    rows={6}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.description.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="requirements">Requirements *</Label>
                  <Textarea
                    id="requirements"
                    {...register("requirements", {
                      required: "Requirements are required",
                    })}
                    placeholder="List the required skills, experience, and qualifications..."
                    rows={4}
                  />
                  {errors.requirements && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.requirements.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="benefits">Benefits & Perks</Label>
                  <Textarea
                    id="benefits"
                    {...register("benefits")}
                    placeholder="Describe the benefits, perks, and what makes this opportunity great..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Company Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building2 className="h-5 w-5 mr-2 text-indigo-600" />
                  Company
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="company">Company Name *</Label>
                  <Input
                    id="company"
                    {...register("companyName", {
                      required: "Company name is required",
                    })}
                    placeholder="e.g. TechCorp Inc."
                  />
                  {errors.companyName && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.companyName.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="companyDescription">
                    Company Description *
                  </Label>
                  <Textarea
                    id="companyDescription"
                    {...register("companyDesc", {
                      required: "Company description is required",
                    })}
                    placeholder="Brief description of the company..."
                    rows={4}
                  />
                  {errors.companyDesc && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.companyDesc.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="companyEmail">
                    Company HR Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="companyEmail"
                    type="email"
                    {...register("companyEmail", {
                      required: "Company HR email is required",
                    })}
                    placeholder="e.g. TechCorp Inc."
                  />
                  {errors.companyEmail && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.companyEmail.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Preview Card */}
            <Card>
              <CardHeader>
                <CardTitle>Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium">Title:</span>
                    <p className="text-gray-600">
                      {watchedValues.title || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium">Company:</span>
                    <p className="text-gray-600">
                      {watchedValues.companyName || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium">Location:</span>
                    <p className="text-gray-600 flex items-center">
                      <MapPin className="h-3 w-3 mr-1" />
                      {watchedValues.location || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium">Salary:</span>
                    <p className="text-gray-600 flex items-center">
                      <IndianRupee className="h-3 w-3 mr-1" />
                      {watchedValues.salaryStart && watchedValues.salaryEnd
                        ? `${watchedValues.salaryStart} - ${watchedValues.salaryEnd}`
                        : "Not specified"}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium">Status:</span>
                    <p
                      className={`text-sm font-medium ${
                        jobStatus === JobStatusEnum.Published
                          ? "text-green-600"
                          : "text-yellow-600"
                      }`}
                    >
                      {jobStatus === JobStatusEnum.Published
                        ? "Published"
                        : "Draft"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
